<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import DynamicTableField from './DynamicTableField.vue'
import DynamicFormulatorField from './DynamicFormulatorField.vue'
import DynamicValidationField from './DynamicValidationField.vue'
import DynamicGroup from './DynamicGroup.vue'
import DraggableList from './DraggableList.vue'
import { downloadFile } from '@/app/core/services/files'
import { validateTranslate } from '@/app/core/composable/general'
import { UseValidation } from '@/app/core/composable/validation/useValidation'
import { useI18n } from 'vue-i18n'
import {
  getSelectData,
  TableDataQuery,
  SelectOption
} from '@/app/core/services/get-table'
import {
  DTextAreaField,
  DTextField,
  DTagsField,
  DSelectFieldSearch,
  DMultiselect,
  DToggleField,
  DFileUploader
} from 'front-library'
import CustomDynamicRecord from './CustomDynamicRecord.vue'
import PayrollCodeValidator from '@/app/features/payrolls/components/PayrollCodeValidator.vue'

const props = defineProps({
  count: { type: Number, required: false, default: () => 0 },
  field: { type: Object, required: true, default: () => {} },
  modelValue: {
    type: [String, Boolean, Number, Array, Object]
  },
  validation: {
    type: Object as () => UseValidation,
    required: false,
    default: () => ({})
  },
  error: { type: String, required: false, default: () => '' },
  allModel: { type: Object, required: true, default: () => {} },
  query: {
    type: Object,
    required: false,
    default: () => {}
  },
  readonly: { type: Boolean, required: false, default: false },
  titleBtnSave: { type: String, required: false, default: '' },
  placeholder: { type: String, required: false, default: () => '' },
  hint: { type: String, required: false, default: () => '' },
  dependsOn: {
    type: Object,
    required: false,
    default: () => null
  }
})

const emit = defineEmits(['update:modelValue', 'update:file', 'options', 'block-save'])
const components = new Map<string, any>([
  ['string', { component: DTextField, defaultProps: { type: 'string' } }],
  ['number', { component: DTextField, defaultProps: { type: 'number' } }],
  ['check', { component: DToggleField, defaultProps: {} }],
  ['date', { component: DTextField, defaultProps: { type: 'date' } }],
  ['select', { component: DSelectFieldSearch, defaultProps: {} }],
  ['multiselect', { component: DMultiselect, defaultProps: {} }],
  ['textarea', { component: DTextAreaField, defaultProps: {} }],
  ['file', { component: DFileUploader, defaultProps: {} }],
  ['table', { component: DynamicTableField, defaultProps: {} }],
  ['payrollCodeValidator', { component: PayrollCodeValidator, defaultProps: {} }],
  [
    'formulator',
    {
      component: DynamicFormulatorField,
      defaultProps: {
        amount: props.count,
        notSavedFormulas: props.field?.props?.not_saved_formulas ?? false
      }
    }
  ],
  ['validation', { component: DynamicValidationField, defaultProps: {} }],
  [
    'dynamicGroup',
    { component: DynamicGroup, defaultProps: { type: 'object' } }
  ],
  ['draggableList', { component: DraggableList, defaultProps: {} }],
  ['tagsfield', { component: DTagsField, defaultProps: {} }],
  ['customDynamicRecord', { component: CustomDynamicRecord, defaultProps: {} }]
])

const isLoading = ref(false)
const options = ref<SelectOption[]>([])
const selectSearchText = ref('')
const { t } = useI18n()

const optionsFiltered = computed<SelectOption[]>(() =>
  options.value.filter(item =>
    item.name
      .toLowerCase()
      .includes(selectSearchText.value.toString().toLowerCase())
  )
)

const updateOptions = async (val: any = {}) => {
  if (
    !props.query ||
    props.field.type === 'table' ||
    props.field.type === 'draggableList'
  )
    return

  if (props.query && props.query.options) {
    options.value = props.query.options
    return
  }

  isLoading.value = true
  const params = new Map<string, string>()
  if (val.name && val.value) {
    params.set(val.name, val.value)
  }

  options.value = await getSelectData<SelectOption[]>(
    (props?.dependsOn?.query || props.query) as TableDataQuery,
    params,
    props.modelValue as string,
    val.operator
  )

  if (props.field.type === 'select' && !props.field.allowNull) {
    options.value.unshift({ id: null, name: t('form.button.select') })
  }
  isLoading.value = false
}

const registerDependsOn = () => {
  if (!props.dependsOn) return
  const dependsOn = props.dependsOn
  watch(
    () => props.allModel[dependsOn.field as string],
    (newVal: string) => {
      if (!newVal) return
      updateOptions({
        name: dependsOn.filterTag ?? dependsOn.field,
        value: newVal,
        operator: dependsOn.operator
      }).catch(console.error)
    },
    { deep: true }
  )
  // first load update
  if (props.allModel[dependsOn.field]) {
    updateOptions({
      name: dependsOn.filterTag ?? dependsOn.field,
      value: props.allModel[dependsOn.field],
      operator: dependsOn.operator
    }).catch(console.error)
  }
}

const emitUpdate = (val: any) => {
  if (typeof val === 'string') val = val.trim()
  emit('options', options.value)
  emit('update:modelValue', val)
}

const emitBlockSave = (blocked: boolean) => {
  emit('block-save', blocked)
}

const download = async (urlFile: string) => {
  const nameFile = urlFile.split('.com/')[1]
  const urlDownload: any = await downloadFile(nameFile)
  const link = document.createElement('a')
  document.body.appendChild(link)
  link.href = urlDownload.url
  link.target = '_blank'
  link.click()
}

onMounted(async () => {
  if (!props.dependsOn) {
    await updateOptions()
  }
  registerDependsOn()
})

const dynamicProps = computed(() => {
  let placeholderValue = ''
  if (props.field?.props?.placeholder) {
    placeholderValue = validateTranslate(props.field.props.placeholder)
  } else if (props.field.type === 'select') {
    placeholderValue = t('form.button.select')
  }

  const baseProps = {
    ...(components.get(props.field.type)?.defaultProps ?? {}),
    ...props.field.props,
    placeholder: placeholderValue,
    hint: props.field?.props?.hint
      ? validateTranslate(props.field.props.hint)
      : '',
    headerTitle: props.field?.props?.headerTitle
      ? validateTranslate(props.field.props.headerTitle)
      : '',
    helpText: props.field?.props?.helpText
      ? validateTranslate(props.field.props.helpText)
      : '',
    disabled: props.readonly
      ? props.readonly
      : props.field?.props?.disabled ?? false
  }

  return baseProps
})
</script>
<template>
  <component
    :label="validateTranslate(field.label)"
    :is="components.get(field.type)?.component || 'div'"
    :model-value="modelValue"
    :validation="validation"
    :field="field"
    @update:modelValue="emitUpdate"
    @block-save="emitBlockSave"
    :all-model="allModel"
    :class="field.class"
    :error="error"
    :readonly="readonly"
    v-bind="dynamicProps"
    @filter="selectSearchText = $event"
    :options="optionsFiltered"
    @file="$emit('update:file', $event)"
    :uploadFile="
      titleBtnSave === t('form.button.save')
        ? 'create'
        : titleBtnSave === t('form.button.update')
        ? 'edit'
        : 'view'
    "
    @handle-file-download="download($event)" />
</template>
